import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { Landmark, Loader2, RefreshCw, MapPin, FileText, FileDown, History, AlertTriangle, StopCircle, KeyRound } from "lucide-react";

// Riepilogo compatto dei dati catastali del condominio — usato in TUTTE le intestazioni
export const CatastoHeaderInfo = ({ catasto, className = "" }) => {
  if (!catasto || (!catasto.foglio && !catasto.particella && !catasto.units_count)) return null;
  return (
    <span data-testid="catasto-header-info" className={`inline-flex items-center gap-1 text-xs text-muted-foreground ${className}`}>
      <Landmark className="h-3 w-3 shrink-0 text-[#007BFF]" />
      Catasto: {catasto.comune ? `${catasto.comune} · ` : ""}Fg. {catasto.foglio || "—"} · Part. {catasto.particella || "—"}
      {catasto.units_count ? ` · ${catasto.units_count} immobili` : ""}
    </span>
  );
};

// Riconosce il tipo di diritto reale dalla titolarità catastale.
export const dirittoOf = (titolarita) => {
  const t = (titolarita || "").toLowerCase();
  if (!t) return null;
  if (t.includes("nuda")) return "nuda_proprieta";
  if (t.includes("usufrut")) return "usufrutto";
  if (t.includes("abitazione")) return "abitazione";
  if (t.includes("superficie")) return "superficie";
  if (/\buso\b/.test(t)) return "uso";
  if (t.includes("propriet")) return "proprieta";
  return null;
};

const DIRITTO_LABEL = {
  nuda_proprieta: "Nuda proprietà",
  usufrutto: "Usufrutto",
  abitazione: "Diritto di abitazione",
  superficie: "Diritto di superficie",
  uso: "Diritto d'uso",
  proprieta: "Proprietà",
};

// Formatta la titolarità in modo leggibile mantenendo il tipo di diritto:
// "Nuda proprieta' per 1/2" -> "Nuda proprietà 1/2 (50%)"
export const formatQuota = (titolarita) => {
  const t = (titolarita || "").trim();
  if (!t) return "";
  const diritto = dirittoOf(t);
  const label = DIRITTO_LABEL[diritto] || t.replace(/proprieta'?/i, "Proprietà");
  const m = t.match(/(\d+)\s*\/\s*(\d+)/);
  if (m) {
    const num = parseInt(m[1], 10), den = parseInt(m[2], 10) || 1;
    const pct = Math.round((num / den) * 1000) / 10;
    const pctStr = `${pct}`.replace(".", ",");
    return `${label} ${num}/${den} (${pctStr}%)`;
  }
  return label;
};

// Badge visivo per i diritti diversi dalla piena proprietà (in primis la nuda proprietà).
export const DirittoBadge = ({ titolarita, className = "" }) => {
  const d = dirittoOf(titolarita);
  if (!d || d === "proprieta") return null;
  const isNuda = d === "nuda_proprieta";
  return (
    <span
      data-testid="diritto-badge"
      title={isNuda
        ? "Nuda proprietà: il titolare possiede l'immobile ma non ne ha il godimento (spetta all'usufruttuario)"
        : `Diritto reale: ${DIRITTO_LABEL[d]}`}
      className={`inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${isNuda ? "bg-purple-100 text-purple-700 border-purple-200" : "bg-amber-100 text-amber-800 border-amber-200"} ${className}`}>
      <KeyRound className="h-2.5 w-2.5" />{DIRITTO_LABEL[d]}
    </span>
  );
};

// Hook riusabile: gestisce sincronizzazione catastale (bottone + anteprima indirizzo + costo + alert CF).
export const useCatastoSync = (building, residents = [], onSynced) => {
  const [busy, setBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [syncProgress, setSyncProgress] = useState(null);
  const [addrPreview, setAddrPreview] = useState(null);
  const [noCfWarn, setNoCfWarn] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [applying, setApplying] = useState(false);
  const cancelRef = useRef(false);
  const pendingAddrOnly = useRef(false);
  const [syncEstimate, setSyncEstimate] = useState(null);
  const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);
  const [viaOverride, setViaOverride] = useState("");
  const [civicoOverride, setCivicoOverride] = useState("");
  const cfCount = residents.filter((r) => (r.codice_fiscale || "").trim().length >= 11).length;
  const gated = building?.subscription_status !== "attivo";

  useEffect(() => {
    if (!building?.id) return undefined;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/catasto/buildings/${building.id}/address-preview`);
        if (alive) setAddrPreview(r.data);
      } catch { if (alive) setAddrPreview(null); }
      try {
        const s = await api.get(`/catasto/buildings/${building.id}/sync/status`);
        if (alive && (s.data.sync || {}).status === "review" && s.data.sync.proposal) setProposal(s.data.sync.proposal);
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [building?.id]);

  const sync = async (addressOnly = false) => {
    if (cfCount === 0 && !addressOnly) { setNoCfWarn(true); return; }
    pendingAddrOnly.current = addressOnly || cfCount === 0;
    try {
      const r = await api.get(`/catasto/buildings/${building.id}/sync/estimate`);
      const est = r.data || {};
      // Gratis o super admin esente → nessun avviso di costo, parte subito
      if ((est.charge_coin || 0) <= 0 && est.sufficient !== false) {
        runSync(pendingAddrOnly.current);
        return;
      }
      setSyncEstimate(est);
      setConfirmSyncOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile calcolare il costo della ricerca catastale");
    }
  };

  const confirmSyncNow = () => {
    setConfirmSyncOpen(false);
    runSync(pendingAddrOnly.current);
  };

  const runSync = async (addressOnly = false) => {
    cancelRef.current = false;
    setBusy(true); setSyncMsg("Avvio sincronizzazione…");
    try {
      await api.post(`/catasto/buildings/${building.id}/sync`, { link_by_cf: true, address_only: addressOnly || cfCount === 0, via_override: viaOverride || "", civico_override: civicoOverride || "" });
      let done = false; let tries = 0;
      while (!done && tries < 300) {
        await new Promise((res) => setTimeout(res, 3000));
        tries++;
        if (cancelRef.current) { done = true; break; }
        const s = await api.get(`/catasto/buildings/${building.id}/sync/status`);
        const st = s.data.sync || {};
        if (st.units_total && st.phase !== "riepilogo") {
          setSyncProgress({ done: st.units_done || 0, total: st.units_total });
        } else {
          setSyncProgress(null);
        }
        if (st.phase === "riepilogo") {
          setSyncMsg("Preparazione riepilogo…");
        } else if (st.units_total) {
          setSyncMsg(`Elaborazione immobili… ${st.units_done || 0}/${st.units_total}`);
        } else if (st.phase === "collegamento_cf" && st.cf_total) {
          setSyncMsg(`Ricerca per codice fiscale… ${st.cf_done || 0}/${st.cf_total}`);
        } else if (st.phase === "indirizzo") {
          setSyncMsg("Recupero immobili per indirizzo…");
        } else if (st.status === "running") {
          setSyncMsg("Recupero immobili dal Catasto…");
        }
        if (st.status === "review") {
          done = true;
          setProposal(st.proposal || null);
        } else if (st.status === "done") {
          done = true;
          toast.success(`Dati catastali sincronizzati: ${st.units_count || 0} immobili`);
          onSynced?.(s.data.catasto);
        } else if (st.status === "cancelled") {
          done = true;
        } else if (st.status === "error") {
          done = true;
          toast.error(st.error || "Sincronizzazione non riuscita", { duration: 7000 });
        }
      }
      if (!done) toast.message("La sincronizzazione continua in background. Ricarica la pagina tra poco.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sincronizzazione non riuscita", { duration: 7000 });
    } finally { setBusy(false); setSyncMsg(""); setSyncProgress(null); }
  };

  const cancelSync = async () => {
    cancelRef.current = true;
    setSyncMsg("Interruzione in corso…");
    try {
      await api.post(`/catasto/buildings/${building.id}/sync/cancel`);
      toast.message("Sincronizzazione catastale interrotta");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile interrompere la sincronizzazione");
    } finally { setBusy(false); setSyncMsg(""); setSyncProgress(null); }
  };

  const applyProposal = async (excludeIds = []) => {
    setApplying(true);
    try {
      const r = await api.post(`/catasto/buildings/${building.id}/sync/apply`, { exclude_resident_ids: excludeIds });
      toast.success(`Anagrafica aggiornata: ${r.data.created} aggiunti, ${r.data.updated} aggiornati`);
      setProposal(null);
      const s = await api.get(`/catasto/buildings/${building.id}/sync/status`);
      onSynced?.(s.data.catasto);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile applicare le modifiche", { duration: 7000 });
    } finally { setApplying(false); }
  };

  const discardProposal = async () => {
    try { await api.post(`/catasto/buildings/${building.id}/sync/discard`); } catch { /* noop */ }
    setProposal(null);
  };

  return { busy, syncMsg, syncProgress, addrPreview, cfCount, gated, noCfWarn, setNoCfWarn, sync, cancelSync,
           syncEstimate, confirmSyncOpen, setConfirmSyncOpen, confirmSyncNow,
           viaOverride, setViaOverride, civicoOverride, setCivicoOverride,
           proposal, setProposal, applyProposal, discardProposal, applying };
};

// Box completo: sincronizzazione + elenco subalterni (pannello admin, anagrafica condominio)
export const CatastoBox = ({ building, residents = [], onSynced }) => {
  const [busy, setBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [visuraBusy, setVisuraBusy] = useState(null);
  const [visure, setVisure] = useState([]);
  const [dlBusy, setDlBusy] = useState(null);
  const [dupWarn, setDupWarn] = useState(null);
  const [refreshTarget, setRefreshTarget] = useState(null);
  const [addrPreview, setAddrPreview] = useState(null);
  const [noCfWarn, setNoCfWarn] = useState(false);
  const catasto = building.catasto;
  const gated = building.subscription_status !== "attivo";
  const catastoAllowed = ["premium", "elite", "b"].includes((building.subscription_option || "").toLowerCase().trim());
  const planBlocked = !gated && !catastoAllowed;
  const cfCount = residents.filter((r) => (r.codice_fiscale || "").trim().length >= 11).length;

  const loadVisure = useCallback(async () => {
    try {
      const r = await api.get(`/catasto/buildings/${building.id}/visure`);
      setVisure(r.data.visure || []);
    } catch { /* ignora */ }
  }, [building.id]);

  useEffect(() => { loadVisure(); }, [loadVisure]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/catasto/buildings/${building.id}/address-preview`);
        if (alive) setAddrPreview(r.data);
      } catch { if (alive) setAddrPreview(null); }
    })();
    return () => { alive = false; };
  }, [building.id]);

  const sync = async (addressOnly = false) => {
    if (cfCount === 0 && !addressOnly) { setNoCfWarn(true); return; }
    setBusy(true); setSyncMsg("Avvio sincronizzazione…");
    try {
      await api.post(`/catasto/buildings/${building.id}/sync`, { link_by_cf: true, address_only: addressOnly || cfCount === 0 });
      let done = false; let tries = 0;
      while (!done && tries < 200) {
        await new Promise((res) => setTimeout(res, 3000));
        tries++;
        const s = await api.get(`/catasto/buildings/${building.id}/sync/status`);
        const st = s.data.sync || {};
        if (st.units_total) {
          setSyncMsg(`Elaborazione immobili… ${st.units_done || 0}/${st.units_total}`);
        } else if (st.phase === "collegamento_cf" && st.cf_total) {
          setSyncMsg(`Collegamento proprietari per Codice Fiscale… ${st.cf_done || 0}/${st.cf_total}`);
        } else if (st.status === "running") {
          setSyncMsg("Recupero immobili dal Catasto…");
        }
        if (st.status === "done") {
          done = true;
          toast.success(`Dati catastali sincronizzati: ${st.units_count || 0} immobili`);
          onSynced?.(s.data.catasto);
        } else if (st.status === "error") {
          done = true;
          toast.error(st.error || "Sincronizzazione non riuscita", { duration: 7000 });
        }
      }
      if (!done) toast.message("La sincronizzazione continua in background. Ricarica la pagina tra poco per vedere i dati aggiornati.");
    } catch (e) {
      const d = e.response?.data?.detail || "Sincronizzazione non riuscita";
      toast.error(d, { duration: 7000 });
    } finally { setBusy(false); setSyncMsg(""); }
  };

  const downloadVisura = async (sessionId, force = false) => {
    setDlBusy(sessionId);
    try {
      const r = await api.get(`/catasto/visura/${sessionId}/document${force ? "?force=true" : ""}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "visura_catastale.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      if (force) { toast.success("Visura aggiornata scaricata dal Catasto"); loadVisure(); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download non riuscito");
    } finally { setDlBusy(null); }
  };

  const startCheckout = async (unit) => {
    setVisuraBusy(unit.id_immobile);
    try {
      const r = await api.post("/catasto/visura/checkout", {
        building_id: building.id, id_immobile: unit.id_immobile, origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile avviare il pagamento", { duration: 7000 });
      setVisuraBusy(null);
    }
  };

  const buyVisura = async (unit) => {
    if (!unit.id_immobile) { toast.error("Immobile senza identificativo catastale: aggiorna i dati."); return; }
    // Avviso anti-doppione: visura per lo stesso immobile acquistata negli ultimi 30 giorni
    const existing = visure
      .filter((v) => v.id_immobile === unit.id_immobile)
      .sort((a, b) => new Date(b.paid_at || b.created_at) - new Date(a.paid_at || a.created_at))[0];
    if (existing) {
      const days = (Date.now() - new Date(existing.paid_at || existing.created_at)) / 86400000;
      if (days <= 30) { setDupWarn({ unit, existing }); return; }
    }
    startCheckout(unit);
  };

  const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return s; }
  };

  const units = catasto?.units || [];

  return (
    <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="catasto-box">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-head text-lg font-semibold flex items-center gap-2"><Landmark className="h-5 w-5 text-[#007BFF]" />Dati catastali — {building.name}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Scarica gli immobili partendo dal <strong>codice fiscale</strong> di ogni condòmino e incrociando l'indirizzo del condominio: l'immobile che combacia viene salvato in anagrafica.
            {catasto?.last_synced_at && <> Ultimo aggiornamento: {new Date(catasto.last_synced_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} ({catasto.environment}).</>}
          </p>
          {busy && syncMsg && (
            <p className="text-xs text-[#007BFF] mt-1 flex items-center gap-1" data-testid="catasto-sync-progress">
              <Loader2 className="h-3 w-3 animate-spin" />{syncMsg}
            </p>
          )}
        </div>
        <Button data-testid="catasto-sync-btn" onClick={() => sync()} disabled={busy || gated || planBlocked}
          title={gated ? "Condominio non collegato: attivalo per scaricare i dati catastali" : planBlocked ? "Il piano Essenziale non include la sincronizzazione catastale: passa a Premium o Elite" : ""}
          className="bg-[#007BFF] hover:bg-[#0056B3] h-9 disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          {catasto ? "Aggiorna dati catastali" : "Scarica e integra dati catastali"}
        </Button>
      </div>

      {planBlocked && (
        <div className="px-6 py-2.5 border-b border-border text-xs bg-amber-500/10 text-amber-700 dark:text-amber-500 flex items-start gap-2" data-testid="catasto-plan-blocked">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>La <strong>sincronizzazione catastale</strong> non è inclusa nel piano <strong>Essenziale</strong> di questo condominio. Passa al piano <strong>Premium</strong> o <strong>Elite</strong> da "Acquisti e collegamento" per attivarla.</span>
        </div>
      )}

      {addrPreview && (
        <div className={`px-6 py-2.5 border-b border-border text-xs flex items-start gap-2 ${addrPreview.via && addrPreview.civico ? "bg-[#007BFF]/5" : "bg-amber-500/10"}`} data-testid="catasto-address-preview">
          {addrPreview.via && addrPreview.civico ? (
            <>
              <MapPin className="h-3.5 w-3.5 text-[#007BFF] shrink-0 mt-0.5" />
              <span>La ricerca catastale userà → Via: <strong>{addrPreview.via}</strong> · Civico: <strong>{addrPreview.civico}</strong>{addrPreview.comune ? <> · Comune: <strong>{addrPreview.comune}</strong>{addrPreview.provincia ? ` (${addrPreview.provincia})` : ""}</> : null}</span>
            </>
          ) : (
            <>
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
              <span className="text-amber-700 dark:text-amber-500">
                {!addrPreview.via ? "Indirizzo del condominio mancante o non valido. " : "Numero civico non riconosciuto nell'indirizzo. "}
                Completa l'indirizzo (via + civico) nell'Anagrafica del condominio per una ricerca catastale precisa
                {addrPreview.via && !addrPreview.civico ? " — altrimenti verranno scaricati tutti gli immobili dell'intera via." : "."}
              </span>
            </>
          )}
        </div>
      )}

      {!gated && !planBlocked && (
        cfCount > 0 ? (
          <div className="px-6 py-2 border-b border-border text-xs text-muted-foreground bg-secondary/40" data-testid="catasto-cf-info">
            Verranno interrogati <strong>{cfCount}</strong> condòmini con codice fiscale · costo fisso <strong>3 SedioCoin</strong> per sincronizzazione.
          </div>
        ) : null
      )}

      {catasto && (
        <div className="px-6 py-3 border-b border-border text-sm flex flex-wrap gap-x-6 gap-y-1" data-testid="catasto-summary">
          <span className="text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{catasto.comune} ({catasto.provincia})</span>
          <span>Foglio <strong>{catasto.foglio || "—"}</strong></span>
          <span>Particella <strong>{catasto.particella || "—"}</strong></span>
          <span>Immobili <strong>{catasto.units_count || 0}</strong></span>
        </div>
      )}

      {units.length > 0 ? (
        <div className="overflow-x-auto scroll-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3 font-semibold">Sub.</th>
                <th className="px-5 py-3 font-semibold">Categoria</th>
                <th className="px-5 py-3 font-semibold">Classe</th>
                <th className="px-5 py-3 font-semibold">Consistenza</th>
                <th className="px-5 py-3 font-semibold">Rendita</th>
                <th className="px-5 py-3 font-semibold">Indirizzo</th>
                <th className="px-5 py-3 font-semibold text-right">Visura</th>
              </tr>
            </thead>
            <tbody>
              {units.map((u, i) => (
                <tr key={i} data-testid={`catasto-unit-${i}`} className="border-b border-border last:border-0 hover:bg-secondary/50">
                  <td className="px-5 py-3 font-semibold text-[#007BFF]">{u.subalterno || "—"}</td>
                  <td className="px-5 py-3 text-muted-foreground">{u.categoria || "—"}</td>
                  <td className="px-5 py-3 text-muted-foreground">{u.classe || "—"}</td>
                  <td className="px-5 py-3 text-muted-foreground">{u.consistenza || "—"}</td>
                  <td className="px-5 py-3 text-muted-foreground">{u.rendita || "—"}</td>
                  <td className="px-5 py-3 text-muted-foreground text-xs">{u.indirizzo || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <Button data-testid={`catasto-visura-btn-${i}`} size="sm" variant="outline"
                      onClick={() => buyVisura(u)} disabled={gated || !u.id_immobile || visuraBusy === u.id_immobile}
                      title={gated ? "Condominio non collegato" : "Acquista e scarica la visura catastale (€8,00)"}
                      className="h-8 rounded-md whitespace-nowrap">
                      {visuraBusy === u.id_immobile ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1.5" />}
                      Visura PDF · €8
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : catasto ? (
        <div className="p-6 text-center text-muted-foreground text-sm">Nessun immobile trovato per questo indirizzo. Verifica indirizzo/provincia del condominio.</div>
      ) : (
        <div className="p-6 text-center text-muted-foreground text-sm">
          Nessun dato catastale ancora scaricato. {gated ? "Collega il condominio per attivare questa funzione." : "Usa \"Scarica e integra dati catastali\"."}
        </div>
      )}

      {visure.length > 0 && (
        <div className="border-t border-border" data-testid="visure-history">
          <div className="px-6 py-3 flex items-center gap-2 text-sm font-semibold">
            <History className="h-4 w-4 text-[#007BFF]" />Visure acquistate
            <span className="text-xs font-normal text-muted-foreground">({visure.length})</span>
          </div>
          <div className="divide-y divide-border">
            {visure.map((v) => (
              <div key={v.session_id} data-testid={`visura-history-${v.session_id}`} className="px-6 py-3 flex items-center justify-between gap-3 hover:bg-secondary/50">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{v.unit_label || "Immobile"}</div>
                  <div className="text-xs text-muted-foreground">
                    Acquistata il {fmtDate(v.paid_at || v.created_at)} · €{((v.amount || 800) / 100).toFixed(2).replace(".", ",")}
                    {v.refreshed_at && <> · <span className="text-[#007BFF]">Aggiornata il {fmtDate(v.refreshed_at)}</span></>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button data-testid={`visura-redownload-${v.session_id}`} size="sm" variant="outline"
                    onClick={() => downloadVisura(v.session_id)} disabled={dlBusy === v.session_id}
                    className="h-8 rounded-md whitespace-nowrap">
                    {dlBusy === v.session_id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
                    Scarica PDF
                  </Button>
                  <Button data-testid={`visura-refresh-${v.session_id}`} size="sm" variant="ghost"
                    onClick={() => setRefreshTarget(v)} disabled={dlBusy === v.session_id}
                    title="Richiedi una visura aggiornata dal Catasto (ignora la copia salvata)"
                    className="h-8 rounded-md whitespace-nowrap">
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Aggiorna
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AlertDialog open={!!dupWarn} onOpenChange={(o) => { if (!o) setDupWarn(null); }}>
        <AlertDialogContent data-testid="visura-dup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />Visura già acquistata di recente
            </AlertDialogTitle>
            <AlertDialogDescription>
              Hai già acquistato una visura per questo immobile ({dupWarn?.existing?.unit_label}) il{" "}
              <b>{dupWarn ? fmtDate(dupWarn.existing.paid_at || dupWarn.existing.created_at) : ""}</b>.
              Puoi scaricare di nuovo quella esistente gratuitamente, oppure acquistarne una nuova (€8,00).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <Button data-testid="visura-dup-download" variant="outline"
              onClick={() => { const s = dupWarn.existing.session_id; setDupWarn(null); downloadVisura(s); }}>
              <FileDown className="h-4 w-4 mr-2" />Scarica quella esistente
            </Button>
            <Button data-testid="visura-dup-buy" className="bg-[#007BFF] hover:bg-[#0056B3]"
              onClick={() => { const u = dupWarn.unit; setDupWarn(null); startCheckout(u); }}>
              <FileText className="h-4 w-4 mr-2" />Acquista comunque · €8
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!refreshTarget} onOpenChange={(o) => { if (!o) setRefreshTarget(null); }}>
        <AlertDialogContent data-testid="visura-refresh-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-[#007BFF]" />Richiedi visura aggiornata
            </AlertDialogTitle>
            <AlertDialogDescription>
              Verrà richiesta al Catasto una <b>nuova visura</b> per {refreshTarget?.unit_label}, ignorando la copia
              salvata, così da avere i dati più recenti. L'operazione potrebbe richiedere qualche secondo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRefreshTarget(null)}>Annulla</Button>
            <Button data-testid="visura-refresh-confirm" className="bg-[#007BFF] hover:bg-[#0056B3]"
              onClick={() => { const s = refreshTarget.session_id; setRefreshTarget(null); downloadVisura(s, true); }}>
              <RefreshCw className="h-4 w-4 mr-2" />Aggiorna e scarica
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={noCfWarn} onOpenChange={setNoCfWarn}>
        <AlertDialogContent data-testid="catasto-no-cf-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />Codice fiscale mancante
            </AlertDialogTitle>
            <AlertDialogDescription>
              Nessun condòmino ha il <b>codice fiscale</b> in anagrafica. Posso comunque cercare <b>per indirizzo</b>:
              recupero i proprietari degli immobili al civico, li abbino ai nomi e calcolo il codice fiscale quando
              possibile. Costo fisso: <b>3 SedioCoin</b> per la sincronizzazione.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" data-testid="catasto-no-cf-cancel" onClick={() => setNoCfWarn(false)}>Annulla</Button>
            <Button data-testid="catasto-no-cf-address" className="bg-[#007BFF] hover:bg-[#0056B3]"
              onClick={() => { setNoCfWarn(false); sync(true); }}>Cerca per indirizzo</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
